/**
 * Page détail événement découverte — /discovery/events/:id
 * Affiche : nom, date/heure, lieu, distance, organisateur, catégorie, prix,
 * description, image, recommandation IA, boutons actions, menu secondaire.
 */

import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box, CircularProgress, Alert, Button } from '@mui/material'
import './Discovery.css'
import {
  getDiscoveryEventDetail,
  recordEventInteraction,
  addEventToCalendar,
  removeEventFromCalendar,
  getShareUrl,
} from '../../utils/discoveryApi'

const EventDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [event, setEvent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isSaved, setIsSaved] = useState(false)
  const [inCalendar, setInCalendar] = useState(false)
  const [loadingActions, setLoadingActions] = useState({})
  const [showMenu, setShowMenu] = useState(false)

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true)
        const res = await getDiscoveryEventDetail(id)
        if (res?.status === 200 && res.data) {
          setEvent(res.data)
          setIsSaved(res.data.is_saved || false)
          setInCalendar(res.data.in_calendar || false)
          // POST interaction OPEN
          recordEventInteraction(id, 'OPEN', { origin: 'detail' }).catch(() => {})
        } else {
          setError(t('discovery.eventNotFound'))
        }
      } catch (err) {
        setError(t('discovery.errorLoading'))
      } finally {
        setLoading(false)
      }
    }
    fetchEvent()
  }, [id, t])

  const handleInteraction = async (type, action = null) => {
    setLoadingActions((p) => ({ ...p, [type]: true }))
    try {
      await recordEventInteraction(id, type, { origin: 'detail' })
      if (type === 'INTERESTED') {
        // Success feedback
      } else if (type === 'NOT_INTERESTED') {
        // Show confirmation & go back
        setTimeout(() => navigate(-1), 500)
      } else if (type === 'CATEGORY_MORE' || type === 'CATEGORY_LESS') {
        // Preferences updated
      } else if (type === 'ORGANIZER_BLOCK') {
        // Blocked
      }
    } catch (err) {
      console.error('Interaction failed:', err)
    } finally {
      setLoadingActions((p) => ({ ...p, [type]: false }))
    }
  }

  const handleSave = async () => {
    setLoadingActions((p) => ({ ...p, save: true }))
    try {
      const type = isSaved ? 'UNSAVE' : 'SAVE'
      await recordEventInteraction(id, type, { origin: 'detail' })
      setIsSaved(!isSaved)
    } catch (err) {
      console.error('Save failed:', err)
    } finally {
      setLoadingActions((p) => ({ ...p, save: false }))
    }
  }

  const handleCalendar = async () => {
    setLoadingActions((p) => ({ ...p, calendar: true }))
    try {
      if (inCalendar) {
        await removeEventFromCalendar(id)
      } else {
        await addEventToCalendar(id)
      }
      setInCalendar(!inCalendar)
    } catch (err) {
      console.error('Calendar action failed:', err)
    } finally {
      setLoadingActions((p) => ({ ...p, calendar: false }))
    }
  }

  const handleShare = async () => {
    setLoadingActions((p) => ({ ...p, share: true }))
    try {
      const res = await getShareUrl(id)
      if (res?.status === 200) {
        const url = res.data.url
        if (navigator.share) {
          navigator.share({
            title: res.data.title,
            text: res.data.text,
            url: window.location.origin + url,
          })
        } else {
          // Fallback: copy to clipboard
          const fullUrl = window.location.origin + url
          navigator.clipboard.writeText(fullUrl)
            .then(() => {
              // Show feedback toast/alert
              console.log('URL copied to clipboard')
            })
            .catch(() => console.error('Copy failed'))
        }
      }
    } catch (err) {
      console.error('Share failed:', err)
    } finally {
      setLoadingActions((p) => ({ ...p, share: false }))
    }
  }

  const handleBlockOrganizer = async () => {
    const confirmed = window.confirm(t('discovery.confirmBlockOrganizer'))
    if (confirmed) {
      await handleInteraction('ORGANIZER_BLOCK')
      setShowMenu(false)
    }
  }

  if (loading) {
    return (
      <Box className="dsc-page">
        <CircularProgress />
      </Box>
    )
  }

  if (error || !event) {
    return (
      <Box className="dsc-page">
        <Alert severity="error">{error || t('discovery.eventNotFound')}</Alert>
        <Button onClick={() => navigate(-1)}>{t('buttons.back')}</Button>
      </Box>
    )
  }

  const category = event.category || {}
  const place = event.place || {}
  const organizer = event.organizer || {}
  const candidate = event.candidate || {}
  const pricingLabel =
    event.pricing_type === 'FREE' ? t('discovery.free') : (
      event.price_min !== null && event.price_max !== null
        ? `${event.price_min.toFixed(2)}–${event.price_max.toFixed(2)} ${event.currency}`
        : event.price_min !== null
          ? `Dès ${event.price_min.toFixed(2)} ${event.currency}`
          : t('discovery.priceUnknown')
    )

  const startDate = new Date(event.start_datetime)
  const endDate = event.end_datetime ? new Date(event.end_datetime) : null
  const dateStr = event.all_day
    ? startDate.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
    : `${startDate.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} ${startDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`

  return (
    <Box className="dsc-page dsc-detail">
      <Box className="dsc-header">
        <Button onClick={() => navigate(-1)} className="dsc-back-btn">
          ← {t('buttons.back')}
        </Button>
      </Box>

      {event.image_url && (
        <Box className="dsc-image" style={{ backgroundImage: `url(${event.image_url})` }} />
      )}

      <Box className="dsc-content">
        <h1>{event.canonical_name}</h1>

        <Box className="dsc-meta">
          <div className="dsc-meta-row">
            <span className="dsc-label">{t('discovery.when')}</span>
            <span>{dateStr}</span>
          </div>

          {place.name && (
            <div className="dsc-meta-row">
              <span className="dsc-label">{t('discovery.where')}</span>
              <span>
                {place.name}
                {place.city ? `, ${place.city}` : ''}
                {candidate.distance_km !== null ? ` (${candidate.distance_km.toFixed(1)} km)` : ''}
              </span>
            </div>
          )}

          {organizer.name && (
            <div className="dsc-meta-row">
              <span className="dsc-label">{t('discovery.organizer')}</span>
              <span>{organizer.name}</span>
            </div>
          )}

          {category.label && (
            <div className="dsc-meta-row">
              <span className="dsc-label">{t('discovery.category')}</span>
              <span>{category.label}</span>
            </div>
          )}

          <div className="dsc-meta-row">
            <span className="dsc-label">{t('discovery.pricing')}</span>
            <span>{pricingLabel}</span>
          </div>
        </Box>

        {event.short_description && (
          <Box className="dsc-description">
            <h3>{t('discovery.description')}</h3>
            <p>{event.short_description}</p>
          </Box>
        )}

        {event.description && (
          <Box className="dsc-description">
            <p>{event.description}</p>
          </Box>
        )}

        {candidate.recommendation_reason && (
          <Box className="dsc-recommendation">
            <h3>{t('discovery.whyRecommended')}</h3>
            <p>{candidate.recommendation_reason}</p>
          </Box>
        )}

        {event.attribution && event.attribution.length > 0 && (
          <Box className="dsc-attribution">
            <h4>{t('discovery.source')}</h4>
            {event.attribution.map((attr, idx) => (
              <a key={idx} href={attr.url} target="_blank" rel="noopener noreferrer">
                {attr.source}
              </a>
            ))}
          </Box>
        )}

        <Box className="dsc-actions">
          <Button
            className="dsc-action-btn dsc-action-interested"
            onClick={() => handleInteraction('INTERESTED')}
            disabled={loadingActions.INTERESTED}
          >
            👍 {t('discovery.interested')}
          </Button>

          <Button
            className="dsc-action-btn dsc-action-not-interested"
            onClick={() => handleInteraction('NOT_INTERESTED')}
            disabled={loadingActions.NOT_INTERESTED}
          >
            👎 {t('discovery.notInterested')}
          </Button>

          <Button
            className="dsc-action-btn"
            onClick={handleSave}
            disabled={loadingActions.save}
          >
            {isSaved ? '⭐' : '☆'} {isSaved ? t('discovery.saved') : t('discovery.save')}
          </Button>

          <Button
            className="dsc-action-btn"
            onClick={handleCalendar}
            disabled={loadingActions.calendar}
          >
            {inCalendar ? '📅' : '📅'} {inCalendar ? t('discovery.inCalendar') : t('discovery.addToCalendar')}
          </Button>

          <Button
            className="dsc-action-btn"
            onClick={handleShare}
            disabled={loadingActions.share}
          >
            {t('discovery.share')}
          </Button>

          {event.official_url && (
            <a href={event.official_url} target="_blank" rel="noopener noreferrer" className="dsc-external-link">
              {t('discovery.officialSite')}
            </a>
          )}

          {event.booking_url && (
            <a
              href={event.booking_url}
              target="_blank"
              rel="noopener noreferrer"
              className="dsc-external-link"
              onClick={async (e) => {
                e.preventDefault()
                await recordEventInteraction(id, 'BOOKING_CLICK', { origin: 'detail' })
                window.open(event.booking_url, '_blank')
              }}
            >
              {t('discovery.book')}
            </a>
          )}
        </Box>

        <Box className="dsc-menu-secondary">
          <button className="dsc-menu-toggle" onClick={() => setShowMenu(!showMenu)}>
            ⋮
          </button>
          {showMenu && (
            <Box className="dsc-menu-items">
              <button onClick={() => handleInteraction('CATEGORY_MORE')}>
                {t('discovery.categoryMore')}
              </button>
              <button onClick={() => handleInteraction('CATEGORY_LESS')}>
                {t('discovery.categoryLess')}
              </button>
              <button onClick={handleBlockOrganizer} className="dsc-menu-danger">
                {t('discovery.blockOrganizer')}
              </button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default EventDetail
